import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import type {
  ISettingsNav,
  ISettingsNavGroup,
  ISettingsNavItem,
} from "@/config/settings-nav.config";
import { stripTrailingSlash } from "@/utils/helper";

const VTAB = "vTab";

export interface IActiveSettingsItem {
  group?: ISettingsNavGroup;
  item?: ISettingsNavItem;
}

const parse = (to: string) => {
  const url = new URL(to, "http://x");
  return { pathname: url.pathname, vTab: url.searchParams.get(VTAB) };
};

/**
 * The sidebar item the current URL shows.
 *
 * Mirrors `VerticalTabs`' own fallback: a section URL with no `vTab`, or one
 * naming a tab that does not exist, renders the section's first tab — so that
 * is the item to highlight. Off a section page, an item's `activeFor` prefixes
 * decide. A path that no item belongs to (the hidden Templates section)
 * highlights nothing.
 */
export const useActiveSettingsItem = (nav: ISettingsNav) => {
  const location = useLocation();
  // `/settings/security-settings/` is the same page as without the slash.
  const pathname = stripTrailingSlash(location.pathname);
  const { search } = location;
  const vTab = new URLSearchParams(search).get(VTAB);

  return useMemo<IActiveSettingsItem>(() => {
    for (const group of nav.groups) {
      const inSection = group.items.filter(
        (item) => parse(item.to).pathname === pathname,
      );
      if (inSection.length === 0) continue;
      const item =
        inSection.find((i) => parse(i.to).vTab === vTab) ?? inSection[0];
      return { group, item };
    }

    // Not a section page — a form route an item claims via `activeFor`.
    let best: IActiveSettingsItem & { length: number } = { length: 0 };
    for (const group of nav.groups) {
      for (const item of group.items) {
        for (const prefix of item.activeFor ?? []) {
          if (pathname.startsWith(prefix) && prefix.length > best.length) {
            best = { group, item, length: prefix.length };
          }
        }
      }
    }
    return { group: best.group, item: best.item };
  }, [nav, pathname, vTab]);
};
